import React, { useState, useEffect } from 'react';
import { getBrokerOrders } from '../api/portfolioApi';
import AccountSidebar from '../components/AccountSidebar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({
    broker: 'ZERODHA',
    status_filter: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Fetch orders on component mount and when filters change
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      
      // Add status filter if provided
      if (filters.status_filter) {
        params.status_filter = filters.status_filter;
      }
      
      console.log('--- DEBUG: OrdersPage fetchOrders called (LIVE DATA) ---');
      console.log('Broker:', filters.broker);
      console.log('Params:', params);
      
      // Fetch LIVE orders from broker API
      const response = await getBrokerOrders(filters.broker, params);
      
      console.log('--- DEBUG: OrdersPage received response ---');
      console.log('Response:', response);
      
      if (response.success) {
        setOrders(response.orders || []);
        setLastFetched(response.fetched_at);
      } else {
        setError('Failed to fetch orders from broker');
      }
    } catch (err) {
      console.error('--- DEBUG: Error caught in OrdersPage component (fetchOrders) ---');
      console.error('Full error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      setError(err.response?.data?.detail || 'Failed to fetch orders from broker. Please ensure your broker is connected.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'COMPLETE': 'bg-green-100 text-green-800',
      'OPEN': 'bg-blue-100 text-blue-800',
      'TRIGGER PENDING': 'bg-yellow-100 text-yellow-800',
      'CANCELLED': 'bg-gray-100 text-gray-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'FAILED': 'bg-red-100 text-red-800',
      'ACTIVE': 'bg-yellow-100 text-yellow-800',
      'TRIGGERED': 'bg-green-100 text-green-800',
      'DISABLED': 'bg-gray-100 text-gray-800',
      'EXPIRED': 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getOrderTypeColor = (orderType) => {
    if (!orderType) return 'bg-gray-100 text-gray-800';
    
    const typeColors = {
      'GTT': 'bg-purple-100 text-purple-800',
      'GTT-OCO': 'bg-purple-100 text-purple-800',
      'LIMIT': 'bg-blue-100 text-blue-800',
      'MARKET': 'bg-indigo-100 text-indigo-800',
      'SL': 'bg-orange-100 text-orange-800',
      'SL-M': 'bg-orange-100 text-orange-800'
    };
    return typeColors[orderType] || 'bg-gray-100 text-gray-800';
  };

  const getTransactionTypeColor = (type) => {
    return type === 'BUY' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: 'url(/assets/profilepage/profile_page_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center py-16 lg:py-12">
        <div className="max-w-6xl w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-4">
              <AccountSidebar activePage="orders" />
              
              <div className="md:col-span-3">
                <div className="bg-white rounded-r-2xl p-8">
                  {/* Header */}
                  <div className="mb-8">
                    <div className="flex justify-between items-start">
                      <div>
                        <h1 className="text-3xl font-bold text-gray-900">Order History</h1>
                        <p className="text-gray-600 mt-1">Live order history from your broker</p>
                        {lastFetched && (
                          <p className="text-sm text-gray-500 mt-1">
                            📡 Last updated: {new Date(lastFetched).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={fetchOrders}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:bg-gray-400"
                      >
                        {loading ? 'Refreshing...' : '🔄 Refresh'}
                      </button>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
                      {error}
                    </div>
                  )}

                  {/* Filters */}
                  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Broker</label>
                        <select
                          value={filters.broker}
                          onChange={(e) => handleFilterChange('broker', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="ZERODHA">Zerodha</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                          value={filters.status_filter}
                          onChange={(e) => handleFilterChange('status_filter', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">All Status</option>
                          <option value="COMPLETE">Complete</option>
                          <option value="OPEN">Open</option>
                          <option value="TRIGGER PENDING">Trigger Pending (GTT Active)</option>
                          <option value="CANCELLED">Cancelled</option>
                          <option value="REJECTED">Rejected</option>
                          <option value="FAILED">Failed</option>
                          <option value="TRIGGERED">Triggered (GTT Executed)</option>
                          <option value="DISABLED">Disabled (GTT)</option>
                          <option value="EXPIRED">Expired (GTT)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-blue-900 mb-2">📊 Complete Order History</h4>
                    <p className="text-sm text-blue-800">
                      This page shows both <strong>regular orders</strong> and <strong>GTT (Good Till Triggered)</strong> orders from your broker.
                      GTT orders are marked in purple and show trigger prices. Data is fetched live from {filters.broker} on every page load.
                    </p>
                  </div>

                  {/* Orders Table */}
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {loading ? (
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">Loading orders...</p>
                      </div>
                    ) : orders.length === 0 ? (
                      <div className="p-12 text-center">
                        <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
                        <p className="text-gray-600">Your order history will appear here</p>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Order ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Symbol
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Order Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Action
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Quantity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Price/Trigger
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Executed
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {orders.map((order) => (
                                <tr key={order.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatDate(order.created_at)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono text-xs">
                                    {order.broker_order_id || '-'}
                                    {order.tag && (
                                      <span className="block text-xs text-purple-600 mt-1">
                                        {order.tag}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                    {order.stock_symbol}
                                    <span className="block text-xs text-gray-500 mt-1">
                                      {order.exchange}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getOrderTypeColor(order.order_type)}`}>
                                      {order.order_type || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTransactionTypeColor(order.transaction_type)}`}>
                                      {order.transaction_type}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {order.quantity || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {order.trigger_price ? (
                                      <span>
                                        {formatCurrency(order.trigger_price)}
                                        <span className="block text-xs text-purple-600">Trigger</span>
                                      </span>
                                    ) : (
                                      formatCurrency(order.price || order.average_price)
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                      {order.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {order.executed_quantity > 0 ? `${order.executed_quantity}` : '-'}
                                    {order.average_price && order.executed_quantity > 0 && (
                                      <span className="block text-xs text-gray-500">
                                        @ {formatCurrency(order.average_price)}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                      {/* Order Count Summary */}
                      {orders.length > 0 && (
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                          <div className="text-sm text-gray-700">
                            Showing {orders.length} order{orders.length !== 1 ? 's' : ''} from {filters.broker}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrdersPage;

